import { Link } from "react-router-dom";
import { CheckCircle2, Users, Award, ShieldCheck, Target } from "lucide-react";
import SectionHeading from "@/components/site/SectionHeading";

export default function AboutPage() {
  return (
    <div>
      <section className="py-16 lg:py-24 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div data-testid="about-hero">
            <div className="text-sm font-semibold tracking-[0.18em] uppercase text-[#081537] mb-3">About Us</div>
            <h1 className="font-[Outfit] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0F172A] text-balance">
              12+ years moving Kerala, one home at a time.
            </h1>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              Founded in Kochi, Move On Packers & Movers has helped over 12,000 families and businesses relocate safely and on time. Our mission is simple: bring world-class care, transparency and discipline to every Indian household&apos;s most stressful day.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-6 max-w-md">
              {[
                { value: "12+", label: "Years" },
                { value: "12K+", label: "Moves" },
                { value: "4.9★", label: "Rating" },
              ].map((s) => (
                <div key={s.label}>
                  <div className="font-[Outfit] text-3xl font-bold text-[#081537]">{s.value}</div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <img
            src="https://images.unsplash.com/photo-1600518464441-9306b00c180a?auto=format&fit=crop&w=900&q=85"
            alt="Our team"
            className="rounded-3xl shadow-xl object-cover w-full h-[420px] lg:h-[520px]"
          />
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10">
          {[
            { Icon: Target, title: "Our Mission", text: "To make relocation predictable, transparent and respectful — treating every customer's belongings as if they were our own." },
            { Icon: Award, title: "Our Promise", text: "Honest pricing, professional crew, zero-damage packing, on-time delivery. If we fail, we make it right." },
          ].map(({ Icon, title, text }) => (
            <div key={title} className="bg-slate-50 rounded-2xl p-8 border border-slate-100">
              <Icon className="w-10 h-10 text-[#081537]" strokeWidth={1.5} />
              <h3 className="font-[Outfit] text-2xl font-semibold text-[#0F172A] mt-4">{title}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading overline="What sets us apart" title="A different kind of moving company." />
          <div className="mt-12 grid md:grid-cols-2 gap-5 max-w-3xl">
            {[
              "Background-verified, uniformed crew",
              "Multi-layer professional packing materials",
              "GPS-tracked closed-body vehicles",
              "Transit insurance on every move",
              "Transparent itemized quotations",
              "Dedicated move coordinator",
              "Free site survey within Kochi",
              "Post-move reassembly included",
            ].map((p) => (
              <div key={p} className="flex items-center gap-3 bg-white rounded-xl p-4 border border-slate-100">
                <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-slate-700">{p}</span>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <Link to="/quote" className="inline-flex items-center gap-2 bg-[#081537] hover:bg-[#0F2152] text-white rounded-full px-7 py-3.5 font-semibold transition" data-testid="about-quote-btn">
              Request a Private Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
